import { useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import { findIndex } from 'lodash';

import { Page, WIDGET_TYPE } from '../../types';
import { CollectionLayoutOptions } from '../../types/widgets/collections';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { GUIDE_KEYS } from '../../queries/guides';
import { defaultCollectionWidgetSettings } from '../../components/widgets/helpers';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';
import {
  addNewCollectionWidgetToSectionApi,
  editCollectionStyleApi,
  editCollectionWidgetApi,
} from '../../helpers/api/widgets/collections';
import { useInspectorStore } from '../../stores/inspectorStore';

export const useCollectionWidgetMutations = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const sectionId = useGuideStore(s => s.activeSectionId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number;
  const columnIndex = useInspectorStore(
    s => s.widgetSettings?.columnIndex,
  ) as number;
  const widgetId = useInspectorStore(s => s.widgetSettings?.widgetId) as string;

  return {
    addCollectionWidget: useMutation(
      ({
        newWidgetId,
        collectionId,
        columnIndex,
      }: {
        newWidgetId: string;
        collectionId: string;
        columnIndex: number;
      }) => {
        return addNewCollectionWidgetToSectionApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          newWidgetId,
          columnIndex,
          activeGuideVersion,
          collectionId,
          defaultCollectionWidgetSettings,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyAddCollection(
            activeGuideId,
            activePageId,
            sectionId,
            variables.columnIndex,
            variables.newWidgetId,
            variables.collectionId,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Collection widget added', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editStyle: useMutation(
      (mutationOptions: { style: string }) => {
        return editCollectionStyleApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId ?? '',
          columnIndex,
          activeGuideVersion,
          mutationOptions.style,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateWidgetStyle(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId ?? '',
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editCollectionWidget: useMutation(
      ({
        collectionId,
        layout,
        assetsShown,
        showAssetTitle,
        showAssetType,
        showAssetDownload,
        showCollectionDownload,
        allowAssetRedirection,
        allowCollectionRedirection,
        showOpenAssetInAssetBank,
      }: EditCollectionSettings) => {
        return editCollectionWidgetApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId ?? '',
          columnIndex,
          activeGuideVersion,
          collectionId ?? '',
          layout,
          showAssetTitle,
          assetsShown,
          showAssetType,
          showAssetDownload,
          showCollectionDownload,
          allowAssetRedirection,
          allowCollectionRedirection,
          showOpenAssetInAssetBank,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateCollectionWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId ?? '',
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Collection widget edited', data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

export interface EditCollectionSettings {
  collectionId?: string;
  layout: CollectionLayoutOptions;
  assetsShown: number;
  showAssetTitle: boolean;
  showAssetType: boolean;
  showAssetDownload: boolean;
  showCollectionDownload: boolean;
  allowAssetRedirection: boolean;
  allowCollectionRedirection: boolean;
  showOpenAssetInAssetBank: boolean;
}

const optimisticallyAddCollection = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  collectionId: string,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      if (old) {
        const newWidget = {
          type: WIDGET_TYPE.COLLECTION,
          id: widgetId,
          collectionId,
          collectionSettings: defaultCollectionWidgetSettings,
          style: { stylesheet: '', language: 'CSS', version: 3 },
        };

        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets.push(
            newWidget,
          );
        });
      }
    },
  );
};

const optimisticallyUpdateCollectionWidget = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  widgetProperties: EditCollectionSettings,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const widget =
            old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ];
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = {
            ...widget,
            collectionId: widgetProperties.collectionId,
            collectionSettings: { ...widgetProperties },
          };
        });
      }
    },
  );
};

export const optimisticallyUpdateWidgetStyle = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  style: string,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const widget =
            old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ];
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = {
            ...widget,
            style: { stylesheet: style, language: 'CSS', version: 3 },
          };
        });
      }
    },
  );
};
